import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Building2, Edit, MapPin, Plus, Trash2, Users, Briefcase, Route, Eye, Calendar, ListChecks, Clock, ToggleLeft, ToggleRight, Beaker } from 'lucide-react';
import api, { sidebarAPI } from '../../api.js';
import { PageTemplate } from '../common/PageTemplate.jsx';
import { useTabPermissions } from '../../v3/hooks/useTabPermissions.js';
import OrganizationFormModal from './forms/OrganizationFormModal.jsx';
import WorkingSiteFormModal from './forms/WorkingSiteFormModal.jsx';
import UserFormModal from './forms/UserFormModal.jsx';
import TripFormModal from './forms/TripFormModal.jsx';
import ReportSubscriptionsTab from './ReportSubscriptionsTab.jsx';
// FeatureMonitorTab removed - internal development tool not for customers
import { usePipelineOptional } from '../../v3/context/PipelineContext.jsx';

// Inline Loading Spinner
function LoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center items-center h-64">
      <div className="text-gray-600">{t('common.states.loading')}</div>
    </div>
  );
}

function AdminView({ onMessage }) {
  const { t } = useTranslation();

  // Tab configuration for PageTemplate
  const ADMIN_TABS = [
    { id: 'pipeline', label: t('settings.tabs.pipeline'), icon: ListChecks },
    { id: 'organizations', label: t('settings.tabs.organizations'), icon: Building2 },
    { id: 'sites', label: t('settings.tabs.sites'), icon: MapPin },
    { id: 'users', label: t('settings.tabs.users'), icon: Users },
    { id: 'intermediaries', label: t('settings.tabs.intermediaries'), icon: Briefcase },
    { id: 'trips', label: t('settings.tabs.trips'), icon: Route },
    { id: 'subscriptions', label: t('settings.tabs.subscriptions'), icon: Calendar },
    // feature_monitor removed - internal development tool not for customers
  ];

  const { visibleTabs } = useTabPermissions('admin', ADMIN_TABS);
  const [activeTab, setActiveTab] = useState('organizations');

  // Reset active tab if it's not visible
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);
  const [organizations, setOrganizations] = useState([]);
  const [workingSites, setWorkingSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [orgAccessByUser, setOrgAccessByUser] = useState({}); // user_id -> [{org_code, org_name}] (chantier Groupe)
  const [intermediaries, setIntermediaries] = useState([]);
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showTripModal, setShowTripModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);

  // Pipeline context
  const pipeline = usePipelineOptional();
  const [pipelineToggling, setPipelineToggling] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('flexible');
  const [dayStartTime, setDayStartTime] = useState('09:00');
  const [dayEndTime, setDayEndTime] = useState('18:00');

  // Sidebar Orchestrator (Phase 1 - Feature Flag)
  const [sidebarOrchestratorEnabled, setSidebarOrchestratorEnabled] = useState(sidebarAPI.isEnabled());

  const handleOrchestratorToggle = () => {
    const newValue = !sidebarOrchestratorEnabled;
    sidebarAPI.setEnabled(newValue);
    setSidebarOrchestratorEnabled(newValue);
    onMessage && onMessage({
      title: newValue ? t('settings.pipeline.orchestrator.toastEnabledTitle') : t('settings.pipeline.orchestrator.toastDisabledTitle'),
      message: newValue
        ? t('settings.pipeline.orchestrator.toastEnabledMessage')
        : t('settings.pipeline.orchestrator.toastDisabledMessage'),
      type: 'info'
    });
  };

  // Load data
  const loadOrganizations = async () => {
    try {
      const response = await api.organization.getAll();
      setOrganizations(response.data || []);
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.organizations.loadError'), type: 'error' });
    }
  };

  const loadWorkingSites = async () => {
    try {
      const response = await api.workingSite.getAll();
      setWorkingSites(response.data || []);
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.sites.loadError'), type: 'error' });
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.user.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadOrgAccess = async () => {
    try {
      const response = await api.userOrgAccess.list();
      const grants = response.data || [];
      const grouped = {};
      grants.forEach(g => {
        if (!grouped[g.user_id]) grouped[g.user_id] = [];
        grouped[g.user_id].push({ org_code: g.org_code, org_name: g.org_name });
      });
      setOrgAccessByUser(grouped);
    } catch (error) {
      // Rôle non habilité (non admin/org_admin) ou service indisponible : la
      // colonne « Filiales » reste vide plutôt que de bloquer l'onglet Utilisateurs.
      setOrgAccessByUser({});
    }
  };

  const loadIntermediaries = async () => {
    try {
      const response = await api.supplier.getAll();
      const allSuppliers = response.data || [];
      setIntermediaries(allSuppliers.filter(s => ['broker', 'insurer', 'agent'].includes(s.category)));
    } catch (error) {
      console.error('Error loading intermediaries:', error);
    }
  };

  const loadTrips = async () => {
    try {
      const [tripRes, vehicleRes, driverRes] = await Promise.all([
        api.trip.getAll(),
        api.vehicle.getAll(),
        api.driver.getAll()
      ]);
      setTrips(tripRes.data || []);
      setVehicles(vehicleRes.data || []);
      setDrivers(driverRes.data || []);
    } catch (error) {
      console.error('Error loading trips:', error);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadOrganizations(),
      loadWorkingSites(),
      loadUsers(),
      loadOrgAccess(),
      loadIntermediaries(),
      loadTrips()
    ]);
    setLoading(false);
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteOrg = async (id) => {
    if (!window.confirm(t('settings.organizations.deleteConfirm'))) return;
    try {
      await api.organization.delete(id);
      loadOrganizations();
      onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.organizations.deleteSuccess'), type: 'success' });
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.organizations.deleteError'), type: 'error' });
    }
  };

  const handleDeleteSite = async (id) => {
    if (!window.confirm(t('settings.sites.deleteConfirm'))) return;
    try {
      await api.workingSite.delete(id);
      loadWorkingSites();
      onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.sites.deleteSuccess'), type: 'success' });
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.sites.deleteError'), type: 'error' });
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm(t('settings.users.deleteConfirm'))) return;
    try {
      await api.user.delete(id);
      loadUsers();
      loadOrgAccess();
      onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.users.deleteSuccess'), type: 'success' });
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.users.deleteError'), type: 'error' });
    }
  };

  const handleDeleteIntermediary = async (id) => {
    if (!window.confirm(t('settings.intermediaries.deleteConfirm'))) return;
    try {
      await api.supplier.delete(id);
      loadIntermediaries();
      onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.intermediaries.deleteSuccess'), type: 'success' });
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.intermediaries.deleteError'), type: 'error' });
    }
  };

  const handleDeleteTrip = async (id) => {
    if (!window.confirm(t('settings.trips.deleteConfirm'))) return;
    try {
      await api.trip.delete(id);
      loadTrips();
      onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.trips.deleteSuccess'), type: 'success' });
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.trips.deleteError'), type: 'error' });
    }
  };

  // Helper functions
  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.make} ${vehicle.model} - ${vehicle.license_plate}` : t('settings.trips.vehicleFallback', { id: vehicleId });
  };

  const getDriverInfo = (driverId) => {
    if (!driverId) return '-';
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : t('settings.trips.driverFallback', { id: driverId });
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.code === orgId || o.id === orgId);
    return org ? org.name : orgId;
  };

  // Sync pipeline schedule state from context
  useEffect(() => {
    if (pipeline?.schedule) {
      setScheduleMode(pipeline.schedule.mode || 'flexible');
      setDayStartTime(pipeline.schedule.day_start_time || '09:00');
      setDayEndTime(pipeline.schedule.day_end_time || '18:00');
    }
  }, [pipeline?.schedule]);

  // Handle pipeline toggle
  const handlePipelineToggle = async () => {
    if (!pipeline?.togglePipeline) return;
    setPipelineToggling(true);
    try {
      await pipeline.togglePipeline(!pipeline.pipelineEnabled);
      onMessage && onMessage({
        title: t('common.messages.successTitle'),
        message: pipeline.pipelineEnabled ? t('settings.pipeline.toggle.disabledSuccess') : t('settings.pipeline.toggle.enabledSuccess'),
        type: 'success'
      });
    } catch (err) {
      onMessage && onMessage({
        title: t('common.messages.errorTitle'),
        message: t('settings.pipeline.toggle.error'),
        type: 'error'
      });
    } finally {
      setPipelineToggling(false);
    }
  };

  // Handle schedule update
  const handleScheduleUpdate = async () => {
    if (!pipeline?.updateSchedule) return;
    try {
      await pipeline.updateSchedule({
        mode: scheduleMode,
        day_start_time: dayStartTime,
        day_end_time: dayEndTime
      });
      onMessage && onMessage({
        title: t('common.messages.successTitle'),
        message: t('settings.pipeline.schedule.updateSuccess'),
        type: 'success'
      });
    } catch (err) {
      onMessage && onMessage({
        title: t('common.messages.errorTitle'),
        message: t('settings.pipeline.schedule.updateError'),
        type: 'error'
      });
    }
  };

  // Generate tabs with dynamic badge counts, filtered by profile permissions
  const tabsWithBadges = visibleTabs.map(tab => {
    let badge;
    switch(tab.id) {
      case 'organizations': badge = organizations.length; break;
      case 'sites': badge = workingSites.length; break;
      case 'users': badge = users.length; break;
      case 'intermediaries': badge = intermediaries.length; break;
      case 'trips': badge = trips.length; break;
      default: badge = undefined;
    }
    return { ...tab, badge };
  });

  return (
    <PageTemplate
      icon={Settings}
      iconColor="text-amber-600"
      title={t('settings.page.title')}
      subtitle={t('settings.page.subtitle')}
      accentColor="amber"
      loading={loading}
      tabs={tabsWithBadges}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {/* Pipeline Settings Tab */}
      {activeTab === 'pipeline' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('settings.pipeline.title')}</h2>
          </div>

          {/* Main Toggle */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${pipeline?.pipelineEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <ListChecks className={`w-8 h-8 ${pipeline?.pipelineEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('settings.pipeline.mode.title')}</h3>
                  <p className="text-sm text-gray-500">
                    {pipeline?.pipelineEnabled
                      ? t('settings.pipeline.mode.descriptionEnabled')
                      : t('settings.pipeline.mode.descriptionDisabled')}
                  </p>
                </div>
              </div>
              <button
                onClick={handlePipelineToggle}
                disabled={pipelineToggling || !pipeline}
                className={`p-2 rounded-lg transition-colors ${
                  pipelineToggling ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                }`}
                title={pipeline?.pipelineEnabled ? t('settings.pipeline.mode.disableTitle') : t('settings.pipeline.mode.enableTitle')}
              >
                {pipeline?.pipelineEnabled ? (
                  <ToggleRight className="w-12 h-12 text-green-500" />
                ) : (
                  <ToggleLeft className="w-12 h-12 text-gray-400" />
                )}
              </button>
            </div>

            {/* Status indicator */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  pipeline?.pipelineEnabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {pipeline?.pipelineEnabled ? t('settings.pipeline.status.enabled') : t('settings.pipeline.status.disabled')}
                </span>
                {pipeline?.pipelineEnabled && pipeline?.sessionState && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    pipeline.sessionState === 'SESSION_OPEN'
                      ? 'bg-blue-100 text-blue-800'
                      : pipeline.sessionState === 'SESSION_CLOSED'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {pipeline.sessionState === 'SESSION_OPEN' ? t('settings.pipeline.status.sessionOpen') :
                     pipeline.sessionState === 'SESSION_CLOSED' ? t('settings.pipeline.status.sessionClosed') :
                     t('settings.pipeline.status.noSession')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar Orchestrator Toggle (Phase 1 - Internal Testing) */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${sidebarOrchestratorEnabled ? 'bg-purple-100' : 'bg-gray-100'}`}>
                  <Beaker className={`w-8 h-8 ${sidebarOrchestratorEnabled ? 'text-purple-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('settings.pipeline.orchestrator.title')}
                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      {t('settings.pipeline.orchestrator.badge')}
                    </span>
                  </h3>
                  <p className="text-sm text-gray-500">
                    {sidebarOrchestratorEnabled
                      ? t('settings.pipeline.orchestrator.descriptionEnabled')
                      : t('settings.pipeline.orchestrator.descriptionDisabled')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleOrchestratorToggle}
                className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                title={sidebarOrchestratorEnabled ? t('settings.pipeline.mode.disableTitle') : t('settings.pipeline.mode.enableTitle')}
              >
                {sidebarOrchestratorEnabled ? (
                  <ToggleRight className="w-12 h-12 text-purple-500" />
                ) : (
                  <ToggleLeft className="w-12 h-12 text-gray-400" />
                )}
              </button>
            </div>

            {/* Status indicator */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  sidebarOrchestratorEnabled
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {sidebarOrchestratorEnabled ? t('settings.pipeline.status.enabled') : t('settings.pipeline.status.disabled')}
                </span>
                <span className="text-xs text-gray-500">
                  {t('settings.pipeline.orchestrator.reloadHint')}
                </span>
              </div>
            </div>
          </div>

          {/* Schedule Settings (only if pipeline enabled) */}
          {pipeline?.pipelineEnabled && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-blue-500" />
                <h3 className="text-lg font-semibold text-gray-900">{t('settings.pipeline.schedule.title')}</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Schedule Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.pipeline.schedule.modeLabel')}
                  </label>
                  <select
                    value={scheduleMode}
                    onChange={(e) => setScheduleMode(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="flexible">{t('settings.pipeline.schedule.modeFlexible')}</option>
                    <option value="fixed">{t('settings.pipeline.schedule.modeFixed')}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {scheduleMode === 'flexible'
                      ? t('settings.pipeline.schedule.hintFlexible')
                      : t('settings.pipeline.schedule.hintFixed')}
                  </p>
                </div>

                {/* Time settings (visible in fixed mode) */}
                {scheduleMode === 'fixed' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.pipeline.schedule.startTime')}
                      </label>
                      <input
                        type="time"
                        value={dayStartTime}
                        onChange={(e) => setDayStartTime(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.pipeline.schedule.endTime')}
                      </label>
                      <input
                        type="time"
                        value={dayEndTime}
                        onChange={(e) => setDayEndTime(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Save button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleScheduleUpdate}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {t('settings.pipeline.schedule.saveButton')}
                </button>
              </div>
            </div>
          )}

          {/* Info card */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 className="font-semibold text-blue-800 mb-2">{t('settings.pipeline.info.title')}</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>1. {t('settings.pipeline.info.step1')}</li>
              <li>2. {t('settings.pipeline.info.step2')}</li>
              <li>3. {t('settings.pipeline.info.step3')}</li>
              <li>4. {t('settings.pipeline.info.step4')}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('settings.organizations.title')}</h2>
            <button
              onClick={() => { setEditingOrg(null); setShowOrgModal(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('settings.organizations.addButton')}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.organizations.columns.code')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.organizations.columns.name')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.organizations.columns.type')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.organizations.columns.city')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.organizations.columns.status')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.organizations.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {organizations.map((org) => (
                    <tr key={org.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">{org.code}</td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{org.name}</div>
                        <div className="text-sm text-gray-500">{org.legal_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          org.org_type === 'holding' ? 'bg-purple-100 text-purple-800' :
                          org.org_type === 'subsidiary' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {org.org_type === 'holding' ? t('settings.orgForm.orgTypes.holding') :
                           org.org_type === 'subsidiary' ? t('settings.orgForm.orgTypes.subsidiary') :
                           org.org_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{org.city || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          org.status === 'active' ? 'bg-green-100 text-green-800' :
                          org.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {org.status === 'active' ? t('settings.orgForm.orgStatus.active') :
                           org.status === 'suspended' ? t('settings.orgForm.orgStatus.suspended') :
                           t('settings.orgForm.orgStatus.closed')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setEditingOrg(org); setShowOrgModal(true); }}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                          >
                            <Edit className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteOrg(org.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {organizations.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  {t('settings.organizations.empty')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Working Sites Tab */}
      {activeTab === 'sites' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('settings.sites.title')}</h2>
            <button
              onClick={() => { setEditingSite(null); setShowSiteModal(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('settings.sites.addButton')}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.code')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.name')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.type')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.city')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.capacity')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.status')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.sites.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {workingSites.map((site) => (
                    <tr key={site.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">{site.site_code}</td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{site.site_name}</div>
                        <div className="text-sm text-gray-500">{site.address}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          site.site_type === 'depot' ? 'bg-blue-100 text-blue-800' :
                          site.site_type === 'garage' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {site.site_type === 'depot' ? t('sites.types.depot', { defaultValue: 'Dépôt' }) :
                           site.site_type === 'garage' ? t('sites.types.garage', { defaultValue: 'Garage' }) :
                           site.site_type === 'parking_lot' ? t('sites.types.parking', { defaultValue: 'Parking' }) :
                           t(`sites.types.${site.site_type}`, { defaultValue: site.site_type })}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{site.city}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {site.parking_capacity ? t('settings.sites.capacitySlots', { count: site.parking_capacity }) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          site.status === 'active' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {site.status === 'active' ? t('settings.siteForm.siteStatus.active') : t('settings.siteForm.siteStatus.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setEditingSite(site); setShowSiteModal(true); }}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                          >
                            <Edit className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteSite(site.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {workingSites.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  {t('settings.sites.empty')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('settings.users.title')}</h2>
            <button
              onClick={() => { setEditingUser(null); setShowUserModal(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('settings.users.addButton')}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.id')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.email')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.name')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.role')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.organization')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('admin.orgAccess.columnHeader')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.status')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.users.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">{user.id}</td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{user.email}</div>
                        <div className="text-sm text-gray-500">{user.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">{user.first_name} {user.last_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.is_superuser ? 'bg-purple-100 text-purple-800' :
                          user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'manager' ? 'bg-green-100 text-green-800' :
                          user.role === 'driver' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.is_superuser ? t('settings.roles.superAdmin') :
                           user.role === 'admin' ? t('settings.roles.admin') :
                           user.role === 'manager' ? t('settings.roles.manager') :
                           user.role === 'driver' ? t('settings.roles.driver') :
                           user.role === 'mechanic' ? t('settings.roles.mechanic') :
                           user.role === 'viewer' ? t('settings.roles.viewer') :
                           user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {user.primary_org_id ? getOrgName(user.primary_org_id) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        {(orgAccessByUser[user.id] || []).length === 0 ? (
                          <span className="text-sm text-gray-400">{t('admin.orgAccess.none')}</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {(orgAccessByUser[user.id] || []).slice(0, 3).map(a => (
                              <span
                                key={a.org_code}
                                title={a.org_name}
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100"
                              >
                                {a.org_code}
                              </span>
                            ))}
                            {(orgAccessByUser[user.id] || []).length > 3 && (
                              <span
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                                title={(orgAccessByUser[user.id] || []).slice(3).map(a => a.org_code).join(', ')}
                              >
                                {t('admin.orgAccess.morePlus', { count: (orgAccessByUser[user.id] || []).length - 3 })}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' :
                          user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {user.status === 'active' ? t('settings.userForm.userStatus.active') :
                           user.status === 'suspended' ? t('settings.userForm.userStatus.suspended') :
                           user.status || t('settings.users.unknownStatus')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                            title={t('common.actions.edit')}
                          >
                            <Edit className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition"
                            title={t('common.actions.delete')}
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  {t('settings.users.empty')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Intermediaries Tab */}
      {activeTab === 'intermediaries' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('settings.intermediaries.title')}</h2>
            <button
              onClick={() => onMessage && onMessage({ title: t('common.messages.infoTitle'), message: t('settings.intermediaries.createNotImplemented'), type: 'info' })}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('settings.intermediaries.addButton')}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.id')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.code')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.name')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.category')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.contact')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.email')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.city')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.intermediaries.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {intermediaries.map((inter) => (
                    <tr key={inter.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">{inter.id}</td>
                      <td className="px-6 py-4 font-mono text-sm text-gray-500">{inter.code || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{inter.name}</div>
                        <div className="text-sm text-gray-500">{inter.company_name || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          inter.category === 'broker' ? 'bg-blue-100 text-blue-800' :
                          inter.category === 'insurer' ? 'bg-purple-100 text-purple-800' :
                          inter.category === 'agent' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {inter.category === 'broker' ? t('settings.intermediaries.categories.broker') :
                           inter.category === 'insurer' ? t('settings.intermediaries.categories.insurer') :
                           inter.category === 'agent' ? t('settings.intermediaries.categories.agent') :
                           inter.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{inter.contact_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{inter.email || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{inter.city || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => onMessage && onMessage({ title: t('common.messages.infoTitle'), message: t('settings.intermediaries.viewDetail', { id: inter.id }), type: 'info' })}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                          >
                            <Eye className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteIntermediary(inter.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {intermediaries.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  {t('settings.intermediaries.empty')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Trips Tab */}
      {activeTab === 'trips' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">{t('settings.trips.title')}</h2>
            <button
              onClick={() => { setEditingTrip(null); setShowTripModal(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md transition flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('settings.trips.addButton')}
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.id')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.vehicle')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.driver')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.dates')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.destination')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.mileage')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.status')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">{t('settings.trips.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {trips.map((trip) => (
                    <tr key={trip.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900">{trip.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{getVehicleInfo(trip.vehicle_id)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{getDriverInfo(trip.driver_id)}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{trip.start_date}</div>
                        {trip.end_date && (
                          <div className="text-sm text-gray-500">→ {trip.end_date}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{trip.destination || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {trip.start_mileage ? `${trip.start_mileage.toLocaleString()} km` : '-'}
                        </div>
                        {trip.end_mileage && (
                          <div className="text-sm text-green-600">
                            → {trip.end_mileage.toLocaleString()} km
                            <span className="text-gray-500 ml-1">
                              (+{(trip.end_mileage - trip.start_mileage).toLocaleString()})
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          trip.status === 'completed' ? 'bg-green-100 text-green-800' :
                          trip.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          trip.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {trip.status === 'completed' ? t('settings.trips.statuses.completed') :
                           trip.status === 'in_progress' ? t('settings.trips.statuses.in_progress') :
                           trip.status === 'cancelled' ? t('settings.trips.statuses.cancelled') :
                           trip.status === 'planned' ? t('settings.trips.statuses.planned') :
                           trip.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setEditingTrip(trip); setShowTripModal(true); }}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                            title={t('common.actions.edit')}
                          >
                            <Edit className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteTrip(trip.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition"
                            title={t('common.actions.delete')}
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {trips.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  {t('settings.trips.empty')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <ReportSubscriptionsTab onMessage={onMessage} />
        </div>
      )}

      {/* Feature Monitor Tab removed - internal development tool not for customers */}

      {/* Organization Form Modal */}
      {showOrgModal && (
        <OrganizationFormModal
          isOpen={showOrgModal}
          onClose={() => { setShowOrgModal(false); setEditingOrg(null); }}
          onSuccess={() => { loadOrganizations(); setShowOrgModal(false); setEditingOrg(null); }}
          onMessage={onMessage}
          editingOrg={editingOrg}
          organizations={organizations}
        />
      )}

      {/* Working Site Form Modal */}
      {showSiteModal && (
        <WorkingSiteFormModal
          isOpen={showSiteModal}
          onClose={() => { setShowSiteModal(false); setEditingSite(null); }}
          onSuccess={() => { loadWorkingSites(); setShowSiteModal(false); setEditingSite(null); }}
          onMessage={onMessage}
          editingSite={editingSite}
          organizations={organizations}
        />
      )}

      {/* User Form Modal */}
      {showUserModal && (
        <UserFormModal
          isOpen={showUserModal}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
          onSuccess={() => { loadUsers(); loadOrgAccess(); setShowUserModal(false); setEditingUser(null); }}
          onMessage={onMessage}
          editingUser={editingUser}
          organizations={organizations}
        />
      )}

      {/* Trip Form Modal */}
      {showTripModal && (
        <TripFormModal
          isOpen={showTripModal}
          onClose={() => { setShowTripModal(false); setEditingTrip(null); }}
          onSuccess={() => { loadTrips(); setShowTripModal(false); setEditingTrip(null); }}
          onMessage={onMessage}
          editingTrip={editingTrip}
          vehicles={vehicles}
          drivers={drivers}
        />
      )}
    </PageTemplate>
  );
}

export default AdminView;
